import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useDataView } from "../../../contexts/data.view.context";

// Default main navigation
const defaultNavigation = [
  { label: "Monitoring", to: `/Monitoring` },
  //{ label: "File Transfer", to: `/FileTransferHistoryView` },
  { label: "Admin", to: `/Admin` },
  { label: "Config", to: `/Config` },
];

// Subtabs
const tabsMap = {
  "/Monitoring": [
    { label: "Real-time", to: `/UnifiedEventSummary` },
    { label: "File", to: `/UnifiedFileSummary` },
    { label: "Batch", to: `/UnifiedBatchProcessSummary` },
    { label: "Cron Jobs", to: `/UnifiedJobRunSummary` },
  ],
  "/FileTransferHistoryView": [
    { label: "File Transfer History", to: `/FileTransferHistoryView` },
    { label: "Stats by Transfer", to: `/FileTrnsStatsViewByTrnsID` },
    { label: "Stats by Project", to: `/FileTrnsStatsViewByProject` },
    { label: "Stats by Group", to: `/FileTrnsStatsViewByGroup` },
    { label: "RMQ API Uploads", to: `/RmqApiUploadHistoryView` },
  ],
  "/Admin": [
    { label: "Caches", to: `/EICaches` },
    { label: "Remarque", to: `/RemarqueEvents` },
    { label: "Query", to: `/QueryInfo` },
    { label: "RedHat", to: `/RedHatProcess` },
    { label: "Error Details", to: `/ErrorLogDetails` },
  ],
  "/OuraPatients": [
    { label: "Patients", to: `/OuraPatients` },
    { label: "Add", to: `/AddOuraPatient` },
  ],
  "/Config": [
    { label: "Connections", to: `/EIConnection` },
    { label: "File", to: `/FileTransfers` },
    { label: "API Configs", to: `/APIConfigs` },
    { label: "Service Configs", to: `/ServiceConfigs` },
    { label: "TASK SCHEDULES", to: `/TaskSchedules` },
  ],
};

const Navigation = () => {
  const userInfo = JSON.parse(sessionStorage.getItem("user") || "{}");
  const email = userInfo.email || "";
  const domain = email.split("@")[1];
  const incyte_user = userInfo.groups?.includes("OURA_INCY216559");

  const navigate = useNavigate();
  const { navigationIndex, setNavigationIndex } = useDataView();

  // Load saved indexes
  const storedMain = parseInt(sessionStorage.getItem("mainNavIndex"), 10);
  const storedSub = parseInt(sessionStorage.getItem("subNavIndex"), 10);

  const [navigation, setNavigation] = useState(defaultNavigation);
  const [navigationLoaded, setNavigationLoaded] = useState(false);

  // Compute current main tab path
  const mainPath = navigation[navigationIndex.main]?.to;

  // Compute subtabs dynamically
  const subMenuTabs = useMemo(() => {
    if (!mainPath) return [];
    return tabsMap[mainPath] || [];
  }, [mainPath]);

  // FIRST LOAD — apply user/domain overrides
  useEffect(() => {
    if (navigationLoaded) return;

    let nav = defaultNavigation;
    let mainIndex = !isNaN(storedMain) ? storedMain : 0;
    let subIndex = !isNaN(storedSub) ? storedSub : 0;

    if (isNaN(storedMain)) {
      // First login, no saved state
      if (incyte_user) {
        nav = [{ label: "Oura Ring", to: `/OuraPatients` }];
        mainIndex = 0;
        subIndex = 0;
      } else if (domain === "remarquesystems.com") {
        nav = [
          { label: "Monitoring", to: `/Monitoring` },
          { label: "File Transfer", to: `/FileTransferHistoryView` },
        ];
        mainIndex = 0;
        subIndex = 0;
      }
    }

    setNavigation(nav);
    setNavigationIndex({ main: mainIndex, sub: subIndex });
    setNavigationLoaded(true);
  }, [
    incyte_user,
    domain,
    storedMain,
    storedSub,
    navigationLoaded,
    setNavigationIndex,
  ]);

  // 🚨 FIXED EFFECT — only run when main tab changes, not on URL change
  useEffect(() => {
    if (!navigationLoaded) return;
    if (subMenuTabs.length === 0) return;

    const subIndex =
      navigationIndex.sub < subMenuTabs.length ? navigationIndex.sub : 0;

    // Navigate ONLY when main tab changes
    navigate(subMenuTabs[subIndex].to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationIndex.main, navigationLoaded]); // 👈 FIXED

  // Main tab click
  const handleChange = (ev, mainIndex) => {
    sessionStorage.setItem("mainNavIndex", mainIndex);
    sessionStorage.setItem("subNavIndex", 0);

    setNavigationIndex({ main: mainIndex, sub: 0 });

    const firstSub = tabsMap[navigation[mainIndex].to]?.[0];
    if (firstSub) navigate(firstSub.to);
    else navigate(navigation[mainIndex].to);
  };

  // Subtab click
  const handleSubTabsChange = (ev, subIndex) => {
    sessionStorage.setItem("subNavIndex", subIndex);
    setNavigationIndex({ main: navigationIndex.main, sub: subIndex });
    navigate(subMenuTabs[subIndex].to);
  };

  return (
    <Container>
      <Tabs
        value={navigationIndex.main}
        onChange={handleChange}
        TabIndicatorProps={{ style: { backgroundColor: "#FFFFFF" } }}
      >
        {navigation.map((nav) => (
          <Tab key={nav.to} label={nav.label} style={{ color: "#FFFFFF" }} />
        ))}
      </Tabs>

      <Tabs
        value={navigationIndex.sub}
        onChange={handleSubTabsChange}
        TabIndicatorProps={{ style: { backgroundColor: "#70db70" } }}
      >
        {subMenuTabs.map((tab) => (
          <Tab key={tab.to} label={tab.label} style={{ color: "#70db70" }} />
        ))}
      </Tabs>
    </Container>
  );
};

export default Navigation;

const Container = styled.div`
  margin: 0 5em;
`;
